import React, { useMemo } from 'react';
import { MessageCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { cabeNoLinkWhatsapp } from '../lib/mensagemCobranca';
import {
    abrirWhatsappVazio, copyToClipboard, ehCelular, linkAppWhatsapp, shareWhatsapp,
} from '../lib/clipboardShare';

const BOTAO = 'flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-xs font-bold transition-colors disabled:opacity-50';

/** O navegador tem a janela de compartilhar do sistema (Web Share)? */
const temCompartilhar = (): boolean =>
    typeof navigator !== 'undefined'
    && typeof navigator.share === 'function'
    && (navigator.canShare?.({ text: 'x' }) ?? true);

/**
 * Manda um texto pelo WhatsApp.
 *
 * O problema é o tamanho. A folha de rota passa de 18 mil caracteres, e link
 * nenhum leva isso: `wa.me/?text=` falha calado acima de ~1.800, e o Chrome do
 * Windows descarta link de app acima de 2.048. Com link, o WhatsApp abria VAZIO
 * — e "abriu vazio" é o "não funciona no computador" de 18/09/2026: o botão até
 * copiava, mas ninguém colava.
 *
 * A saída é a janela de compartilhar do sistema (`navigator.share`), que não tem
 * limite: no Windows o app do WhatsApp aparece nela (ele se registra para
 * receber texto) e a mensagem chega inteira. No celular é a mesma janela de
 * sempre. Link só quando a mensagem cabe nele, ou quando o navegador não tem a
 * janela de compartilhar.
 *
 * Não há botão do WhatsApp Web: ele não aparece na janela de compartilhar e,
 * com texto desse tamanho, só funcionaria colando.
 *
 * Toda saída copia a mensagem também: se o WhatsApp abrir sem ela, é Ctrl+V.
 */
const EnviarWhatsapp: React.FC<{ texto: string; desabilitado?: boolean }> = ({ texto, desabilitado }) => {
    const { addToast } = useToast();
    const celular = useMemo(() => ehCelular(), []);
    const compartilha = useMemo(() => temCompartilhar(), []);

    // A cópia começa no próprio clique — depois dele a página perde o foco para
    // a janela de compartilhar ou para o app, e a cópia é recusada.
    // Resolve em true/false; nunca rejeita.
    const copiar = () => copyToClipboard(texto).then(() => true, () => false);

    const avisarCopia = async (copia: Promise<boolean>, aviso: string | null) => {
        if (!(await copia)) addToast('Não consegui copiar. Selecione o texto e copie à mão.', 'error');
        else if (aviso) addToast(aviso, 'info');
    };

    const compartilhar = async (copia: Promise<boolean>) => {
        try {
            await navigator.share({ text: texto });
        } catch (e: any) {
            if (e?.name === 'AbortError') return; // fechou a janela sem escolher
            await avisarCopia(copia, 'Não abriu a janela de compartilhar. A mensagem está copiada: abra o WhatsApp e cole com Ctrl+V.');
        }
    };

    const enviar = () => {
        // No celular, o que cabe no link vai direto para a escolha do contato —
        // um toque a menos que a janela de compartilhar.
        if (celular && cabeNoLinkWhatsapp(texto)) { shareWhatsapp(texto); return; }
        const copia = copiar();
        if (compartilha) { void compartilhar(copia); return; }
        if (celular) {
            abrirWhatsappVazio();
            void avisarCopia(copia, 'Mensagem longa demais para o link — copiei para você colar na conversa.');
            return;
        }
        const link = linkAppWhatsapp(texto);
        window.location.href = link;
        void avisarCopia(copia, link === 'whatsapp://send'
            ? 'Mensagem copiada. No WhatsApp, escolha a conversa e cole com Ctrl+V.'
            : null);
    };

    return (
        <button
            onClick={enviar}
            disabled={desabilitado}
            title={compartilha ? 'Abre a janela de compartilhar — escolha o WhatsApp e a conversa' : undefined}
            className={BOTAO}
        >
            <MessageCircle size={14} /> Enviar no WhatsApp
        </button>
    );
};

export default EnviarWhatsapp;
